import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Button,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import {
  DataGrid,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "contexts/ContextProvider";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import CEQuickFilter from "components/CEQuickFilter";
import { usePageTitle } from "hooks/usePageTitle";
import { numericComparator } from "helpers/ServiceHelpers";
import dayjs from "dayjs";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

const LinguistPaymentProfile = ({ linguistData, onClose }) => {
  usePageTitle("Linguist Payment");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const { showSnackbar, triggerPageRefresh } = useStateContext();

  // Filter state management
  const [openFilter, setOpenFilter] = React.useState(null);
  const [sortModel, setSortModel] = React.useState([]);
  const [filterModel, setFilterModel] = React.useState({ items: [] });

  // 2026-10-22 (Amine): `linguistData` is a snapshot held by the parent list,
  // so flipping a single service would leave this overlay (and its Left/Paid
  // totals) showing the old value until it was closed and reopened. These
  // overrides are layered on top of the snapshot so each toggle is reflected
  // immediately, while `triggerPageRefresh()` keeps the list behind in sync.
  const [paidOverrides, setPaidOverrides] = React.useState({});

  // Extract linguistData properties with defaults to avoid errors when null
  const LinguistName = linguistData?.LinguistName || "";
  const LinguistID = linguistData?.LinguistID || null;
  const LinguistServices = useMemo(
    () => linguistData?.LinguistServices || [],
    [linguistData?.LinguistServices],
  );

  usePageTitle("Linguist Payment: " + LinguistName);

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const handleCopyAmount = () => {
    navigator.clipboard.writeText(AmountLeft.toFixed(2));
    showSnackbar(`Copied left: $${AmountLeft.toFixed(2)}`, theme);
  };

  const handleCopyName = () => {
    navigator.clipboard.writeText(LinguistName);
    showSnackbar(`Copied: ${LinguistName}`, theme);
  };

  const handleOpenLinguistProfile = () => {
    navigate(`/linguists/${LinguistID}`);
  };

  // 2026-10-22 (Amine): a payment recorded by mistake had no way back.
  // This now drives BOTH directions — `paid` false clears the flag.
  //
  // `closeAfter` distinguishes the two callers: a per-service toggle keeps the
  // overlay open (so several rows can be corrected in one visit), while the
  // bulk Mark all buttons keep the original close-and-refresh behaviour.
  const handleMarkAsPaid = async (serviceId, paid = true, closeAfter = false) => {
    try {
      const idsToMark = Array.isArray(serviceId) ? serviceId : [serviceId];
      const validIds = idsToMark.filter(Boolean);
      if (validIds.length === 0) {
        showSnackbar(
          paid ? "No unpaid services to mark" : "No paid services to undo",
          theme,
        );
        return;
      }
      await api.put("/services/mark-linguist-paid", {
        serviceIds: validIds,
        paid,
      });

      // Reflect the change in place — the snapshot prop will not change until
      // the parent list refetches.
      setPaidOverrides((current) => {
        const next = { ...current };
        validIds.forEach((id) => {
          next[id] = paid;
        });
        return next;
      });

      showSnackbar(
        `${validIds.length} service(s) marked as ${paid ? "paid" : "unpaid"}`,
        theme,
      );

      // Keep the list behind the overlay in sync. This is the shared signal
      // every other list screen watches; the previous code called
      // `refreshPage()` as if it were a function, but that value is a boolean
      // state — calling it threw whenever it happened to be truthy.
      triggerPageRefresh();

      if (closeAfter) {
        onClose && onClose(true);
      }
    } catch (error) {
      console.error("Error updating service payment status:", error);
      showSnackbar(
        error.response?.data?.error || "Failed to update payment status",
        theme,
      );
    }
  };

  const handleServiceRowClick = (params) => {
    const serviceId = params.id;
    const category = params.row.ServiceCategory;

    // Route based on service category
    if (category === "Interpretation" || category === "OPI") {
      navigate(`/interpretations/${serviceId}`);
    } else if (category === "Translation") {
      navigate(`/translations/${serviceId}`);
    } else if (category === "Other Service") {
      navigate(`/otherServices/${serviceId}`);
    }
  };

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    const upperFieldName = fieldName.toUpperCase();
    return `LINGUIST_PAYMENT_PROFILE_${LinguistID}_FILTER_${upperFieldName}`;
  }, [LinguistID]);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    const fields = [
      "ServiceCategory",
      "ProjectNumber",
      "Details",
      "Date",
      "TotalLinguistPay",
      "LinguistPaid",
      "Status",
    ];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ServiceCategory",
      "ProjectNumber",
      "Details",
      "Date",
      "TotalLinguistPay",
      "LinguistPaid",
      "Status",
    ];
    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  // Services data rows
  const servicesRows = useMemo(
    () =>
      (LinguistServices || []).map((service) => {
        const category = service.ServiceCategory;
        let status = "";
        if (category === "Interpretation" || category === "OPI") {
          status = service.InterpretationStatus || "";
        } else if (category === "Translation") {
          status = service.TranslationStatus || "";
        } else if (category === "Other Service") {
          status = service.OtherServiceStatus || "";
        } else {
          status =
            service.InterpretationStatus ||
            service.OtherServiceStatus ||
            service.TranslationStatus ||
            "";
        }
        return {
          id: service.id,
          ServiceCategory: category,
          ProjectNumber: service.ProjectNumber,
          Details: service.Details || "N/A",
          Date: service.Date,
          TotalLinguistPay: service.TotalLinguistPay || 0,
          LinguistPaid:
            typeof paidOverrides[service.id] === "boolean"
              ? paidOverrides[service.id]
              : !!service.LinguistPaid,
          Status: status,
        };
      }),
    [LinguistServices, paidOverrides],
  );

  // Totals are derived from the rows above (not from the snapshot) so they
  // stay correct the moment a service is toggled.
  const { AmountPaid, AmountLeft } = useMemo(() => {
    const total = servicesRows.reduce(
      (sum, row) => sum + Number(row.TotalLinguistPay || 0),
      0,
    );
    const paid = servicesRows.reduce(
      (sum, row) =>
        sum + (row.LinguistPaid ? Number(row.TotalLinguistPay || 0) : 0),
      0,
    );
    return { AmountPaid: paid, AmountLeft: total - paid };
  }, [servicesRows]);

  const unpaidServiceIds = useMemo(
    () => servicesRows.filter((s) => !s.LinguistPaid).map((s) => s.id),
    [servicesRows],
  );
  const hasUnpaidServices = unpaidServiceIds.length > 0;

  const paidServiceIds = useMemo(
    () => servicesRows.filter((s) => s.LinguistPaid).map((s) => s.id),
    [servicesRows],
  );
  const hasPaidServices = paidServiceIds.length > 0;

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ServiceCategory",
        valueGetter: (row) => row.ServiceCategory || "",
      },
      { field: "ProjectNumber", valueGetter: (row) => row.ProjectNumber || "" },
      { field: "Details", valueGetter: (row) => row.Details || "N/A" },
      {
        field: "Date",
        valueGetter: (row) =>
          row.Date ? dayjs.tz(row.Date).format("M/D/YYYY") : "N/A",
      },
      {
        field: "TotalLinguistPay",
        valueGetter: (row) =>
          row.TotalLinguistPay
            ? `$${parseFloat(row.TotalLinguistPay).toFixed(2)}`
            : "$0.00",
      },
      {
        field: "LinguistPaid",
        valueGetter: (row) => (row.LinguistPaid ? "Yes" : "No"),
      },
      {
        field: "Status",
        valueGetter: (row) => row.Status || "",
      },
    ],
    [],
  );

  // Column filters using custom hook
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(servicesRows || [], filterColumns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback(
    (field, values) => {
      handleColumnFilterChange(field, values);
      saveColumnFilter(field, values);
    },
    [handleColumnFilterChange, saveColumnFilter],
  );

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();
    setFilterModel({ items: [] });
  }, [clearAllFilters, clearAllColumnFilters]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!servicesRows || servicesRows.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [servicesRows, loadAllColumnFilters]);

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return servicesRows || [];
      }

      return (servicesRows || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;
          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;
          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, servicesRows, filterColumns]);

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const handleToolbarClear = useCallback(() => {
    clearAllFiltersRef.current();
  }, []);

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ hasAnyActiveFilters, onClear, theme }) => (
      <GridToolbarContainer
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }} />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
          }}
        >
          {hasAnyActiveFilters && (
            <Button
              startIcon={<ClearIcon />}
              onClick={onClear}
              variant="outlined"
              size="small"
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.buttonHoverSelected,
                  backgroundColor: theme.palette.primary.buttonHoverSelected,
                  color: theme.palette.background.default,
                },
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
          <CEQuickFilter />
        </Box>
      </GridToolbarContainer>
    );

    return Toolbar;
  }, []);

  // Early return after all hooks
  if (!linguistData) return null;

  // Services DataGrid columns
  const servicesColumns = [
    {
      field: "ServiceCategory",
      headerName: "Category",
      flex: 1,
      minWidth: 100,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ProjectNumber",
      headerName: "Project #",
      flex: 1,
      minWidth: 100,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[1] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      flex: 3,
      minWidth: 300,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      flex: 1,
      minWidth: 100,
      valueGetter: (_, params) => {
        return params.Date ? dayjs.tz(params.Date).format("M/D/YYYY") : "N/A";
      },
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={servicesRows || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TotalLinguistPay",
      headerName: "Amount",
      sortComparator: numericComparator,
      flex: 1,
      minWidth: 100,
      valueGetter: (_, params) => {
        return params.TotalLinguistPay
          ? `$${parseFloat(params.TotalLinguistPay).toFixed(2)}`
          : "$0.00";
      },
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "LinguistPaid",
      headerName: "Linguist Paid",
      flex: 1,
      minWidth: 190,
      valueGetter: (_, params) => (params.LinguistPaid ? "Yes" : "No"),
      sortable: false,
      headerAlign: "center",
      // 2026-10-22 (Amine): the paid state is now a control, not a readout.
      // Clicking either chip flips just that one service, so a single payment
      // can be corrected without touching the rest of the linguist's work.
      renderCell: (params) => {
        const isPaid = Boolean(params.row.LinguistPaid);

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              height: "100%",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                minWidth: 34,
                color: isPaid
                  ? theme.palette.primary.main
                  : theme.palette.error.main,
              }}
            >
              {isPaid ? "Yes" : "No"}
            </Typography>

            <Tooltip title={isPaid ? "Mark as unpaid" : "Mark as paid"}>
              <IconButton
                size="small"
                onClick={(event) => {
                  // The row itself navigates to the service profile — the
                  // toggle must never trigger that.
                  event.stopPropagation();
                  handleMarkAsPaid(params.row.id, !isPaid, false);
                }}
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "8px",
                  color: isPaid
                    ? theme.palette.warning.main
                    : theme.palette.primary.main,
                  border: `1px solid ${
                    isPaid
                      ? `${theme.palette.warning.main}66`
                      : `${theme.palette.primary.main}66`
                  }`,
                  "&:hover": {
                    backgroundColor: isPaid
                      ? `${theme.palette.warning.main}1a`
                      : `${theme.palette.primary.main}1a`,
                  },
                }}
              >
                {isPaid ? (
                  <UndoRoundedIcon sx={{ fontSize: 16 }} />
                ) : (
                  <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[5] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Status",
      headerName: "Status",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => {
        const status = params.row.Status || "";
        let color = theme.palette.primary.text2;
        if (status.startsWith("Confirmed")) {
          color = theme.palette.warning.main;
        } else if (
          status === "Completed" ||
          status === "Delivered" ||
          status === "Published"
        ) {
          color = theme.palette.primary.main;
        } else if (status === "Cancelled" || status === "No Show") {
          color = theme.palette.error.main;
        }
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Typography sx={{ fontWeight: status ? 600 : 400, color }}>
              {status || "—"}
            </Typography>
          </Box>
        );
      },
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[6] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, width: "100%", justifyContent: "flex-end" }}>
      {isMobile ? (
        <>
          <Tooltip title="Open Profile" placement="top">
            <IconButton
              color="primary"
              onClick={handleOpenLinguistProfile}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                },
              }}
            >
              <PersonRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Copy Name" placement="top">
            <IconButton
              color="primary"
              onClick={handleCopyName}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                },
              }}
            >
              <ContentCopyRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Copy Amount" placement="top">
            <IconButton
              color="primary"
              onClick={handleCopyAmount}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                },
              }}
            >
              <ContentCopyRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Mark all as paid" placement="top">
            <span>
              <IconButton
                disabled={!hasUnpaidServices}
                onClick={() => handleMarkAsPaid(unpaidServiceIds, true, true)}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <CheckCircleRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Mark all as unpaid" placement="top">
            <span>
              <IconButton
                disabled={!hasPaidServices}
                onClick={() => handleMarkAsPaid(paidServiceIds, false, true)}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.warning.main,
                  border: `2px solid ${theme.palette.warning.main}`,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: `${theme.palette.warning.main}1a`,
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      theme.palette.action.disabledBackground,
                    borderColor: theme.palette.action.disabled,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <UndoRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ) : (
        <>
          <Button
            color="primary"
            variant="outlined"
            size="medium"
            startIcon={<PersonRoundedIcon />}
            onClick={handleOpenLinguistProfile}
            sx={{
              borderRadius: "12px",
            }}
          >
            Open Profile
          </Button>

          <Button
            color="primary"
            variant="outlined"
            size="medium"
            startIcon={<ContentCopyRoundedIcon />}
            onClick={handleCopyName}
            sx={{
              borderRadius: "12px",
            }}
          >
            Copy Name
          </Button>

          <Button
            color="primary"
            variant="outlined"
            size="medium"
            startIcon={<ContentCopyRoundedIcon />}
            onClick={handleCopyAmount}
            sx={{
              borderRadius: "12px",
            }}
          >
            Copy Amount
          </Button>

          <Button
            color="primary"
            variant="contained"
            size="medium"
            disabled={!hasUnpaidServices}
            onClick={() => handleMarkAsPaid(unpaidServiceIds, true, true)}
            sx={{
              borderRadius: "12px",
            }}
          >
            Mark as Paid
          </Button>

          <Button
            color="warning"
            variant="outlined"
            size="medium"
            startIcon={<UndoRoundedIcon />}
            disabled={!hasPaidServices}
            onClick={() => handleMarkAsPaid(paidServiceIds, false, true)}
            sx={{
              borderRadius: "12px",
            }}
          >
            Mark as Unpaid
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Overlay
      id="linguist-payment-profile"
      open={true}
      onClose={onClose}
      title={LinguistName}
      footer={footerActions}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          p: "1rem 1rem",
          width: "100%",
        }}
      >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: theme.palette.primary.main }}
            >
              Services ({servicesRows.length})
            </Typography>
            <Typography
              variant="h4"
              color={theme.palette.primary.main}
              fontWeight="bold"
            >
              Left: ${AmountLeft.toFixed(2)} - Paid: ${AmountPaid.toFixed(2)}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              p: 0.25,
            }}
          >
            <DataGrid
              rows={columnFilteredData || []}
              columns={servicesColumns}
              pageSizeOptions={[20, 50, 100]}
              filterModel={filterModel}
              onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
              }}
              onRowClick={(params, event, details) => {
                if (openFilter) {
                  setOpenFilter(null);
                  return;
                }
                handleServiceRowClick(params);
              }}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              disableColumnMenu
              disableColumnSorting
              rowSelectionModel={openFilter ? [] : undefined}
              disableRowSelectionOnClick={!!openFilter}
              slots={{ toolbar: CustomToolbar }}
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
                toolbar: {
                  showQuickFilter: true,
                  hasAnyActiveFilters,
                  onClear: handleToolbarClear,
                  theme,
                  printOptions: { disableToolbarButton: true },
                  csvOptions: { disableToolbarButton: true },
                },
              }}
              ignoreDiacritics
              density="compact"
              sx={{
                backgroundColor: theme.palette.background.default,
                borderRadius: "12px",
                border: `1px solid ${theme.palette.divider}`,
                height: "55vh",
                maxHeight: "55vh",
                boxShadow: theme.shadows[2],
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                },
                "& .MuiDataGrid-scrollbarFiller": {
                  backgroundColor: theme.palette.background.alt,
                },
                "& .MuiDataGrid-sortIcon": {
                  display: "none !important",
                },
                "& .MuiDataGrid-menuIconButton": {
                  display: "none !important",
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
                  display: "none !important",
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer":
                  {
                    display: "none !important",
                  },
                "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                  flex: 1,
                },
                "& .MuiDataGrid-columnSeparator": {
                  color: theme.palette.primary.main,
                },
                "& .MuiButton-root": {
                  color: theme.palette.primary.main,
                },
                "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                  outline: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                  {
                    outline: "none !important",
                  },
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main + "20",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main + "30",
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>
    </Overlay>
  );
};

export default LinguistPaymentProfile;
